/**
 * The editor's Meters pane (1.7.4; the Audio side tab before): the sequence preview's sound as broadcast meters, in a
 * pane of its own so they stay visible while editing. Per-channel sample peak bars on a dBFS scale (−60…0, ticks at
 * −18, −9, −6, −3 and 0) with a 2 s peak hold and a sticky over, and EBU R128 loudness as three slim bars on a LUFS
 * scale from −40 to 0: momentary (400 ms), short-term (3 s) and integrated (gated, since the last reset), with a
 * target line (EBU R128 −23, streaming −14, ATSC A/85 −24; usePref editor.loudnessTarget) and amber above it. The
 * readings come from previewAudio.ts while the sequence plays and stay as they were while it is paused.
 * The target and Reset loudness are in the header's cogwheel menu, so the bars take the pane's whole width; the
 * chosen target stays in view as a caption under them.
 */
import { useEffect, useRef, useState } from 'react';
import { Check, RotateCcw, Settings } from 'lucide-react';
import { PaneControls } from '../../components/Panes';
import { usePref } from '../../lib/prefs';
import { useDismissOnOutside } from '../../lib/useDismissOnOutside';
import { LOUDNESS_TARGETS, aboveTarget, loudnessTargetOf, lufsPosition, meterPosition, type LoudnessTarget } from './loudness';
import type { MeterReading, PreviewAudio } from './previewAudio';

const TICKS = [0, -3, -6, -9, -18, -30, -45, -60];
const LUFS_TICKS = [0, -10, -20, -30, -40];
const fmt = (v: number, digits = 1) => (Number.isFinite(v) ? v.toFixed(digits) : '−∞');
/** A target such as −23, with a real minus sign. */
const fmtTarget = (v: number) => String(v).replace('-', '−');
/** A data attribute for tests and tools: the value, or "" for −∞. */
const attr = (v: number) => (Number.isFinite(v) ? v.toFixed(2) : '');

function useReading(audio: PreviewAudio): MeterReading {
  const [r, setR] = useState(() => audio.meter.get());
  useEffect(() => {
    let raf = 0;
    let pending: MeterReading | null = null;
    // Drawn once per animation frame at most, whatever the number of updates.
    const off = audio.meter.subscribe((v) => {
      pending = v;
      if (!raf) raf = requestAnimationFrame(() => { raf = 0; if (pending) setR(pending); });
    });
    return () => { off(); cancelAnimationFrame(raf); };
  }, [audio]);
  return r;
}

/** One slim loudness bar with its value under it. */
function LoudnessBar({ name, short, value, target, testId }: { name: string; short: string; value: number; target: LoudnessTarget; testId: string }) {
  const hot = aboveTarget(value, target);
  return (
    <div className="lufs-channel" title={`${name}: ${fmt(value)} LUFS`}>
      <div className={`lufs-bar ${hot ? 'hot' : ''}`} data-testid={testId} data-lufs={attr(value)} data-hot={hot ? 'true' : 'false'}
        role="meter" aria-label={`${name} loudness`} aria-valuemin={-40} aria-valuemax={0} aria-valuenow={Number.isFinite(value) ? Math.round(value) : -40}>
        <div className="lufs-fill" style={{ height: `${lufsPosition(value) * 100}%` }} />
        <div className="lufs-target" style={{ bottom: `${lufsPosition(LOUDNESS_TARGETS[target].lufs) * 100}%` }} />
      </div>
      <span className="meter-name">{short}</span>
      <span className="meter-value mono">{fmt(value)}</span>
    </div>
  );
}

/**
 * The cogwheel menu (1.7.4 polish): the loudness target as radio items and Reset loudness. Placed on the page (fixed),
 * as RowMenu's list is, so the narrow pane does not cut it off; it follows the button when the pane scrolls, and Escape, a
 * press outside or a window resize closes it.
 */
function MetersSettings({ target, onTarget, onReset }: { target: LoudnessTarget; onTarget: (t: LoudnessTarget) => void; onReset: () => void }) {
  const [at, setAt] = useState<{ top: number; right: number } | null>(null);
  const anchor = useRef<HTMLSpanElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  useDismissOnOutside(anchor, at !== null, () => setAt(null));
  const place = () => {
    const r = button.current?.getBoundingClientRect();
    if (!r) return;
    const top = r.bottom + 4, right = Math.max(8, window.innerWidth - r.right);
    setAt((was) => (was && was.top === top && was.right === right ? was : { top, right }));
  };
  const isOpen = at !== null;
  useEffect(() => {
    if (!isOpen) return;
    const close = () => setAt(null);
    // A scroll that moves the button (the pane's own layout settling) moves the menu with it; others leave it.
    const scrolled = (e: Event) => { if (e.target instanceof Node && button.current && e.target.contains(button.current)) place(); };
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') { setAt(null); button.current?.focus(); } };
    window.addEventListener('scroll', scrolled, true);
    window.addEventListener('resize', close);
    document.addEventListener('keydown', key);
    return () => { window.removeEventListener('scroll', scrolled, true); window.removeEventListener('resize', close); document.removeEventListener('keydown', key); };
  }, [isOpen]);
  const pick = (fn: () => void) => () => { setAt(null); fn(); };
  return (
    <span className="meters-settings" ref={anchor}>
      <button ref={button} type="button" className="btn ghost icon sm" aria-label="Meter settings" title="Loudness target and Reset loudness"
        aria-haspopup="menu" aria-expanded={at !== null} onClick={() => (at ? setAt(null) : place())} data-testid="meters-settings"><Settings size={13} /></button>
      {at && (
        <div className="row-menu-list meters-settings-list" role="menu" aria-label="Meter settings" style={{ top: at.top, right: at.right }} data-testid="meters-settings-list">
          <div className="meters-settings-label" id="loudness-target-label">Loudness target</div>
          <div role="group" aria-labelledby="loudness-target-label" data-testid="loudness-target" data-value={target}
            title="The loudness the programme should reach: the line on the bars; above it they turn amber">
            {(Object.keys(LOUDNESS_TARGETS) as LoudnessTarget[]).map((k) => (
              <button key={k} type="button" role="menuitemradio" aria-checked={k === target} className="row-menu-item meters-settings-item"
                onClick={pick(() => onTarget(k))} data-testid={`loudness-target-${k}`}>
                <span className="meters-settings-check">{k === target && <Check size={13} />}</span>
                <span className="meters-settings-name">{LOUDNESS_TARGETS[k].name}</span> <span className="mono faint">{fmtTarget(LOUDNESS_TARGETS[k].lufs)} LUFS</span>
              </button>
            ))}
          </div>
          <div className="meters-settings-sep" role="separator" />
          <button type="button" role="menuitem" className="row-menu-item meters-settings-item" onClick={pick(onReset)} data-testid="loudness-reset"
            title="Start the integrated loudness again and clear the peak holds and overs">
            <span className="meters-settings-check"><RotateCcw size={13} /></span>Reset loudness
          </button>
        </div>
      )}
    </span>
  );
}

export function MetersPane({ audio }: { audio: PreviewAudio }) {
  const r = useReading(audio);
  const [saved, setTarget] = usePref<string>('editor.loudnessTarget', 'ebu');
  const target = loudnessTargetOf(saved);
  const names = ['L', 'R'];
  return (
    <div className="pane raised meters-pane">
      <div className="viewer-head">
        <span className="pane-title">Meters</span>
        <span style={{ flex: 1 }} />
        <MetersSettings target={target} onTarget={setTarget} onReset={() => audio.resetMeter()} />
        <PaneControls />
      </div>
      <div className="pane-body meters-body audio-meter" data-testid="audio-meter" data-live={r.live ? 'true' : 'false'}>
        <div className="meters-groups">
        <div className="meter-bars">
          <div className="meter-scale" aria-hidden>
            {TICKS.map((t) => <span key={t} style={{ bottom: `${meterPosition(t) * 100}%` }} className={t === -18 ? 'ref' : t === -3 || t === -6 || t === -9 ? 'minor' : ''}>{t}</span>)}
          </div>
          {r.channels.map((c, i) => (
            <div key={names[i]} className="meter-channel">
              <button type="button" className={`meter-over ${c.over ? 'on' : ''}`} title={c.over ? 'This channel reached 0 dBFS. Click to clear.' : 'Lights up when the channel reaches 0 dBFS'}
                onClick={() => audio.resetMeter()} data-testid={`meter-over-${names[i]}`} aria-pressed={c.over}>OVER</button>
              <div className="meter-bar" data-testid={`meter-peak-${names[i]}`} data-db={attr(c.level)} data-hold={attr(c.hold)}
                role="meter" aria-label={`${names[i]} peak`} aria-valuemin={-60} aria-valuemax={0} aria-valuenow={Number.isFinite(c.level) ? Math.round(c.level) : -60}>
                <div className="meter-fill" style={{ height: `${meterPosition(c.level) * 100}%` }} />
                {Number.isFinite(c.hold) && <div className="meter-hold" style={{ bottom: `${meterPosition(c.hold) * 100}%` }} />}
                {TICKS.slice(1, 5).map((t) => <div key={t} className="meter-tick" style={{ bottom: `${meterPosition(t) * 100}%` }} />)}
              </div>
              <span className="meter-name">{names[i]}</span>
              <span className="meter-value mono">{fmt(c.hold)}</span>
            </div>
          ))}
        </div>
        <div className="lufs-bars" title="Loudness (LUFS): momentary, short-term and integrated, on a scale from −40 to 0; the line is the target">
          <div className="lufs-scale" aria-hidden>
            {LUFS_TICKS.map((t) => <span key={t} style={{ bottom: `${lufsPosition(t) * 100}%` }}>{t}</span>)}
          </div>
          <LoudnessBar name="Momentary" short="M" value={r.momentary} target={target} testId="meter-momentary" />
          <LoudnessBar name="Short-term" short="S" value={r.shortTerm} target={target} testId="meter-short" />
          <LoudnessBar name="Integrated" short="I" value={r.integrated} target={target} testId="meter-integrated" />
        </div>
        </div>
        <div className="meters-foot" title="The loudness target, the line on the loudness bars; change it in the cogwheel menu">
          <span className="mono" data-testid="loudness-target-caption"><span className="meters-foot-word">Target </span>{fmtTarget(LOUDNESS_TARGETS[target].lufs)} LUFS</span>
          {!r.live && <span className="help">Play the sequence to measure it.</span>}
        </div>
      </div>
    </div>
  );
}
